import React, { useMemo } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useWindowSize } from 'usehooks-ts';
import { LeftArrow, LeftArrowDark, RightArrow, RightArrowDark } from 'src/assets/images';
import './carousel.scss';

type CarouselProps = {
  hasArrows?: boolean;
  hasScrollbar?: boolean;
  children: React.ReactNode;
};

export const Carousel = ({ hasArrows = false, hasScrollbar = false, children }: CarouselProps) => {
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const [isLeft, setIsLeft] = useState(true);
  const [isRight, setIsRight] = useState(false);

  const { width } = useWindowSize();

  const carouselScrollWidth = useMemo((): number => carouselRef.current?.scrollWidth || 0, [
    carouselRef.current,
    carouselRef.current?.scrollWidth,
  ]);

  const getLeftLimit = (): number =>
    Math.floor(carouselRef.current?.getBoundingClientRect().left || 0);
  const getRightLimit = (): number =>
    Math.ceil(carouselRef.current?.getBoundingClientRect().right || 0);

  useEffect(() => {
    carouselRef.current?.addEventListener('scroll', () =>
      setTimeout(() => {
        const leftX = Math.round(
          carouselRef.current?.firstElementChild?.getBoundingClientRect().x || 0,
        );

        const lastBlock = carouselRef.current?.lastElementChild?.getBoundingClientRect();
        const rightX = Math.round(Number(lastBlock?.x) + Number(lastBlock?.width));

        setIsLeft(leftX >= getLeftLimit());
        setIsRight(rightX <= getRightLimit());
      }, 250),
    );
  }, []);

  const leftArrowClicked = () => carouselRef.current?.scrollBy({ left: -200, behavior: 'smooth' });
  const rightArrowClicked = () => carouselRef.current?.scrollBy({ left: 200, behavior: 'smooth' });

  return (
    <article className="carousel-container">
      {hasArrows && carouselScrollWidth > width && (
        <img
          className={`carousel-button ${isLeft ? 'disabled' : 'enabled'}`}
          src={isLeft ? LeftArrow : LeftArrowDark}
          alt="<"
          onClick={leftArrowClicked}
        />
      )}
      <section id="carousel" className={hasScrollbar ? '' : 'hide-scrollbar'} ref={carouselRef}>
        {children}
      </section>
      {hasArrows && carouselScrollWidth > width && (
        <img
          className={`carousel-button ${isRight ? 'disabled' : 'enabled'}`}
          src={isRight ? RightArrow : RightArrowDark}
          alt=">"
          onClick={rightArrowClicked}
        />
      )}
    </article>
  );
};
